import { datetimeSchema } from './datetime'
import { z } from 'zod'
import {
  getPartnerSearchProgressbarVariant,
  getPartnerSearchProgressName,
  getPartnerSearchReportingFormatLabel,
  getPartnerSearchTypeName,
} from '../utils/partner-search'
import { startCase } from 'lodash'
import { parseDateTime } from '../utils/date-to-string'
import { wrapWithNonNullFallback } from '../utils'

const fileSchema = z.object({
  file_url: z.string(),
  file_name: z.string(),
})

export const claimNumberSchema = z.object({
  number: z.coerce.number(),
  type: z.enum(['independent', 'dependent']),
})

export const partnerSearchSchema = z
  .object({
    claims: z.array(claimNumberSchema).optional(),
    client_email: z.string(),
    client_matter_number: wrapWithNonNullFallback(z.string().nullable(), ''),
    created_at: datetimeSchema,
    currency_at_order: z.string(),
    days_due_in: z.number(),
    due_by: datetimeSchema,
    id: z.string(),
    industry: z.string(),
    instructions: wrapWithNonNullFallback(z.string().nullable(), ''),
    jurisdictions: z.array(z.string()),
    non_patent_literature: z.string().default(''),
    patent_number: z.string().optional(),
    price_at_order: z.number(),
    progress: z.number(),
    reporting_format: z.string(),
    report_urls: z.array(
      z.object({ file_name: z.string(), file_url: z.string() })
    ),
    search_partner: z.string(),
    supporting_file_urls: z.array(fileSchema),
    title: z.string(),
    type: z.string(),
    updated_at: datetimeSchema,
  })
  .transform((data) => {
    const {
      client_email,
      client_matter_number,
      created_at,
      currency_at_order,
      days_due_in,
      due_by,
      non_patent_literature,
      patent_number,
      price_at_order,
      report_urls,
      reporting_format,
      search_partner,
      supporting_file_urls,
      updated_at,
      industry,
      title,
      ...rest
    } = data
    const typeName = getPartnerSearchTypeName(rest.type)
    const progressName = getPartnerSearchProgressName(rest.progress)
    const reportingFormatLabel =
      getPartnerSearchReportingFormatLabel(reporting_format)
    const progressbarVariant = getPartnerSearchProgressbarVariant(rest.progress)
    return {
      ...rest,
      searchType: rest.type,
      createdAt: parseDateTime(created_at),
      updatedAt: parseDateTime(updated_at),
      typeName,
      progressName,
      supportingFiles: supporting_file_urls,
      reportUrls: report_urls,
      searchPartner: search_partner,
      currency: currency_at_order,
      price: price_at_order,
      daysDueIn: days_due_in,
      dueBy: parseDateTime(due_by),
      clientMatterNumber: client_matter_number,
      nonPatentLiterature: non_patent_literature,
      patentNumber: patent_number,
      reportingFormat: reporting_format,
      reportingFormatLabel,
      progressbarVariant,
      clientEmail: client_email,
      industry: startCase(industry),
      title: title || 'null',
    }
  })

export type ApiPartnerSearch = z.infer<typeof partnerSearchSchema>

export const partnerMinifiedSearchSchema = z
  .object({
    id: z.string(),
    title: wrapWithNonNullFallback(z.string().nullable(), ''),
    updated_at: z.string(),
    type: z.string(),
    progress: z.number(),
  })
  .transform((data) => {
    const typeName = getPartnerSearchTypeName(data.type)
    const progressName = getPartnerSearchProgressName(data.progress)
    return {
      ...data,
      updatedAt: data.updated_at,
      progressName,
      typeName,
    }
  })
  .describe('Partner Minified Search Response')

export type PartnerMinifiedSearchType = z.infer<
  typeof partnerMinifiedSearchSchema
>
